/**
 * Achievements catalog — milestone-based unlocks with Neon-Gem + Cosmetic
 * rewards. Each achievement has a `trigger` event-shape that maps to a call
 * site in the codebase (see `bumpAchievement` in gameStore).
 *
 * Two flavours:
 *  - **Threshold**: progress counter against `targetProgress`. Example:
 *    `kills_100` reaches 100 → unlocks. Counter persists across runs.
 *  - **Binary**: progress goes 0 → 1 on first trigger (e.g. `first_legendary`).
 *
 * Trigger types route from the gameStore call site. Keep the type union
 * narrow — adding a new trigger means adding a corresponding call.
 */

export type AchievementTrigger =
  | 'enemy_killed'           // any mob — fired from applyDungeonLoot per kill counter
  | 'boss_killed'            // boss-tier enemy down
  | 'vault_cleared'          // applyDungeonLoot with vaultTier set
  | 'legendary_dropped'      // legendary equipment landed in stash
  | 'first_legendary'        // unique binary
  | 'item_upgraded_to_max'   // L10 reach
  | 'item_ascended'          // any ascensionTier++
  | 'level_reached'          // level milestone — progress = max(progress, newLevel)
  | 'endless_wave_reached'   // best wave milestone
  | 'cd_earned_total'        // lifetime CD earned counter
  | 'cosmetic_unlocked'      // meta — counts cosmetic library size
  | 'all_tier_1_complete';   // highestVaultTier ≥ 1 binary

export interface AchievementReward {
  neonGems?: number;
  cosmeticId?: string;
  /** Free-text shown on unlock toast when no concrete reward (rare). */
  flavorOnly?: boolean;
}

export interface AchievementDef {
  id: string;
  nameKey: string;
  descriptionKey: string;
  iconKey: string;          // Lucide-icon-name (from src/ui/components/Icon.tsx registry)
  trigger: AchievementTrigger;
  /** For threshold achievements: required progress to unlock. Binary = 1. */
  targetProgress: number;
  reward: AchievementReward;
  /** Hidden until unlocked (no spoiler in the list). Default false. */
  hidden?: boolean;
}

export const ACHIEVEMENTS: Record<string, AchievementDef> = {
  // ── Combat: Kill Counters ─────────────────────────────────────
  first_kill: {
    id: 'first_kill', nameKey: 'ach.first_kill', descriptionKey: 'ach.first_kill_desc',
    iconKey: 'sword', trigger: 'enemy_killed', targetProgress: 1,
    reward: { neonGems: 5 },
  },
  kills_100: {
    id: 'kills_100', nameKey: 'ach.kills_100', descriptionKey: 'ach.kills_100_desc',
    iconKey: 'sword', trigger: 'enemy_killed', targetProgress: 100,
    reward: { neonGems: 25 },
  },
  kills_1000: {
    id: 'kills_1000', nameKey: 'ach.kills_1000', descriptionKey: 'ach.kills_1000_desc',
    iconKey: 'sword', trigger: 'enemy_killed', targetProgress: 1000,
    reward: { neonGems: 100, cosmeticId: 'title_collector' },
  },
  kills_5000: {
    id: 'kills_5000', nameKey: 'ach.kills_5000', descriptionKey: 'ach.kills_5000_desc',
    iconKey: 'skull', trigger: 'enemy_killed', targetProgress: 5000,
    reward: { neonGems: 250 },
  },

  // ── Combat: Bosses ────────────────────────────────────────────
  first_boss: {
    id: 'first_boss', nameKey: 'ach.first_boss', descriptionKey: 'ach.first_boss_desc',
    iconKey: 'skull', trigger: 'boss_killed', targetProgress: 1,
    reward: { neonGems: 20, cosmeticId: 'title_boss_slayer' },
  },
  bosses_10: {
    id: 'bosses_10', nameKey: 'ach.bosses_10', descriptionKey: 'ach.bosses_10_desc',
    iconKey: 'skull', trigger: 'boss_killed', targetProgress: 10,
    reward: { neonGems: 75 },
  },
  bosses_50: {
    id: 'bosses_50', nameKey: 'ach.bosses_50', descriptionKey: 'ach.bosses_50_desc',
    iconKey: 'skull', trigger: 'boss_killed', targetProgress: 50,
    reward: { neonGems: 300 },
  },

  // ── Progression: Vault Clears ─────────────────────────────────
  first_vault: {
    id: 'first_vault', nameKey: 'ach.first_vault', descriptionKey: 'ach.first_vault_desc',
    iconKey: 'swords', trigger: 'vault_cleared', targetProgress: 1,
    reward: { neonGems: 15, cosmeticId: 'title_vault_pioneer' },
  },
  vaults_25: {
    id: 'vaults_25', nameKey: 'ach.vaults_25', descriptionKey: 'ach.vaults_25_desc',
    iconKey: 'swords', trigger: 'vault_cleared', targetProgress: 25,
    reward: { neonGems: 80 },
  },
  vaults_100: {
    id: 'vaults_100', nameKey: 'ach.vaults_100', descriptionKey: 'ach.vaults_100_desc',
    iconKey: 'swords', trigger: 'vault_cleared', targetProgress: 100,
    reward: { neonGems: 300 },
  },
  all_tier_1_clear: {
    id: 'all_tier_1_clear', nameKey: 'ach.all_tier_1_clear', descriptionKey: 'ach.all_tier_1_clear_desc',
    iconKey: 'shield_check', trigger: 'all_tier_1_complete', targetProgress: 1,
    reward: { cosmeticId: 'avatar_obsidian', neonGems: 50 },
  },

  // ── Equipment ─────────────────────────────────────────────────
  first_legendary: {
    id: 'first_legendary', nameKey: 'ach.first_legendary', descriptionKey: 'ach.first_legendary_desc',
    iconKey: 'sparkles', trigger: 'first_legendary', targetProgress: 1,
    reward: { neonGems: 30 },
  },
  legendaries_10: {
    id: 'legendaries_10', nameKey: 'ach.legendaries_10', descriptionKey: 'ach.legendaries_10_desc',
    iconKey: 'sparkles', trigger: 'legendary_dropped', targetProgress: 10,
    reward: { neonGems: 150 },
  },
  first_upgrade_max: {
    id: 'first_upgrade_max', nameKey: 'ach.first_upgrade_max', descriptionKey: 'ach.first_upgrade_max_desc',
    iconKey: 'hexagon', trigger: 'item_upgraded_to_max', targetProgress: 1,
    reward: { neonGems: 15 },
  },
  first_ascend: {
    id: 'first_ascend', nameKey: 'ach.first_ascend', descriptionKey: 'ach.first_ascend_desc',
    iconKey: 'sparkles', trigger: 'item_ascended', targetProgress: 1,
    reward: { neonGems: 25 },
  },
  three_ascended: {
    id: 'three_ascended', nameKey: 'ach.three_ascended', descriptionKey: 'ach.three_ascended_desc',
    iconKey: 'sparkles', trigger: 'item_ascended', targetProgress: 3,
    reward: { neonGems: 75 },
  },

  // ── Player Level ──────────────────────────────────────────────
  level_5: {
    id: 'level_5', nameKey: 'ach.level_5', descriptionKey: 'ach.level_5_desc',
    iconKey: 'heart', trigger: 'level_reached', targetProgress: 5,
    reward: { neonGems: 10 },
  },
  level_10: {
    id: 'level_10', nameKey: 'ach.level_10', descriptionKey: 'ach.level_10_desc',
    iconKey: 'heart', trigger: 'level_reached', targetProgress: 10,
    reward: { neonGems: 30 },
  },
  level_25: {
    id: 'level_25', nameKey: 'ach.level_25', descriptionKey: 'ach.level_25_desc',
    iconKey: 'heart', trigger: 'level_reached', targetProgress: 25,
    reward: { neonGems: 100 },
  },
  level_50: {
    id: 'level_50', nameKey: 'ach.level_50', descriptionKey: 'ach.level_50_desc',
    iconKey: 'heart', trigger: 'level_reached', targetProgress: 50,
    reward: { neonGems: 300, cosmeticId: 'avatar_solar_gold' },
  },

  // ── Endless ───────────────────────────────────────────────────
  endless_first: {
    id: 'endless_first', nameKey: 'ach.endless_first', descriptionKey: 'ach.endless_first_desc',
    iconKey: 'zap', trigger: 'endless_wave_reached', targetProgress: 1,
    reward: { neonGems: 10 },
  },
  endless_wave_10: {
    id: 'endless_wave_10', nameKey: 'ach.endless_wave_10', descriptionKey: 'ach.endless_wave_10_desc',
    iconKey: 'zap', trigger: 'endless_wave_reached', targetProgress: 10,
    reward: { neonGems: 40 },
  },
  endless_wave_25: {
    id: 'endless_wave_25', nameKey: 'ach.endless_wave_25', descriptionKey: 'ach.endless_wave_25_desc',
    iconKey: 'zap', trigger: 'endless_wave_reached', targetProgress: 25,
    reward: { neonGems: 120, cosmeticId: 'title_endless_champion' },
  },
  endless_wave_50: {
    id: 'endless_wave_50', nameKey: 'ach.endless_wave_50', descriptionKey: 'ach.endless_wave_50_desc',
    iconKey: 'zap', trigger: 'endless_wave_reached', targetProgress: 50,
    reward: { neonGems: 350 },
  },
  endless_wave_100: {
    id: 'endless_wave_100', nameKey: 'ach.endless_wave_100', descriptionKey: 'ach.endless_wave_100_desc',
    iconKey: 'zap', trigger: 'endless_wave_reached', targetProgress: 100,
    reward: { neonGems: 1000, cosmeticId: 'avatar_void_purple' },
  },

  // ── Currency / Economy ────────────────────────────────────────
  cd_10k: {
    id: 'cd_10k', nameKey: 'ach.cd_10k', descriptionKey: 'ach.cd_10k_desc',
    iconKey: 'gem', trigger: 'cd_earned_total', targetProgress: 10000,
    reward: { neonGems: 20 },
  },
  cd_100k: {
    id: 'cd_100k', nameKey: 'ach.cd_100k', descriptionKey: 'ach.cd_100k_desc',
    iconKey: 'gem', trigger: 'cd_earned_total', targetProgress: 100000,
    reward: { neonGems: 100, cosmeticId: 'title_extractor' },
  },
  cd_1m: {
    id: 'cd_1m', nameKey: 'ach.cd_1m', descriptionKey: 'ach.cd_1m_desc',
    iconKey: 'gem', trigger: 'cd_earned_total', targetProgress: 1_000_000,
    reward: { neonGems: 500 },
  },

  // ── Hidden Achievements ───────────────────────────────────────
  hidden_lucky: {
    id: 'hidden_lucky', nameKey: 'ach.hidden_lucky', descriptionKey: 'ach.hidden_lucky_desc',
    iconKey: 'sparkles', trigger: 'first_legendary', targetProgress: 1,
    reward: { neonGems: 50 }, hidden: true,
  },
  hidden_endless_runner: {
    id: 'hidden_endless_runner', nameKey: 'ach.hidden_endless_runner', descriptionKey: 'ach.hidden_endless_runner_desc',
    iconKey: 'forward', trigger: 'endless_wave_reached', targetProgress: 5,
    reward: { neonGems: 30 }, hidden: true,
  },

  // ── Collector ─────────────────────────────────────────────────
  cosmetics_5: {
    id: 'cosmetics_5', nameKey: 'ach.cosmetics_5', descriptionKey: 'ach.cosmetics_5_desc',
    iconKey: 'circle', trigger: 'cosmetic_unlocked', targetProgress: 5,
    reward: { neonGems: 50 },
  },
  cosmetics_10: {
    id: 'cosmetics_10', nameKey: 'ach.cosmetics_10', descriptionKey: 'ach.cosmetics_10_desc',
    iconKey: 'circle', trigger: 'cosmetic_unlocked', targetProgress: 10,
    reward: { neonGems: 200 },
  },
};

export function getAchievement(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS[id];
}

/** Find all achievement defs that should respond to a given trigger. */
export function achievementsByTrigger(trigger: AchievementTrigger): AchievementDef[] {
  return Object.values(ACHIEVEMENTS).filter((a) => a.trigger === trigger);
}

/** Visible-list of achievements: hidden ones only show after unlock. */
export function visibleAchievements(
  state: Record<string, { unlockedAt?: number; progress: number }> | undefined,
): AchievementDef[] {
  return Object.values(ACHIEVEMENTS).filter((a) => {
    if (!a.hidden) return true;
    return !!state?.[a.id]?.unlockedAt;
  });
}
